import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight, ArrowUpRight } from "lucide-react";
import { BLOG_POSTS } from "@/data/blog";
import { useSeo } from "@/hooks/useSeo";

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS[slug];
  const canonical = `https://termal.datarapor.com/blog/${slug || ""}`;

  useSeo(
    post
      ? {
          title: `${post.title} | ZAKE Termal Blog`,
          description: post.description,
          keywords: post.keywords,
          canonical,
          image: post.cover,
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.description,
            image: post.cover,
            datePublished: post.date,
            dateModified: post.date,
            author: { "@id": "https://termal.datarapor.com/#organization" },
            publisher: { "@id": "https://termal.datarapor.com/#organization" },
            mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
            articleSection: post.category,
            keywords: post.keywords,
          },
        }
      : {}
  );

  if (!post) return <Navigate to="/blog" replace />;

  const others = Object.values(BLOG_POSTS).filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <article data-testid={`blog-post-${slug}`} className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${post.cover})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#06090F] via-[#06090F]/85 to-[#1a0b05]/80" />

        <div className="relative max-w-4xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12">
          <Link
            to="/blog"
            className="inline-flex items-center font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange hover:text-thermal-yellow mb-6"
          >
            ← Blog
          </Link>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-3">
            / {post.category}
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[2rem] sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]"
          >
            {post.title}
          </motion.h1>
          <div className="mt-6 flex flex-wrap items-center gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
            <span className="flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-thermal-orange" /> {post.date}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-thermal-orange" /> {post.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-12 md:py-16 max-w-3xl mx-auto px-6 md:px-12 lg:px-16">
        <p className="text-lg sm:text-xl text-slate-200 leading-relaxed border-l-2 border-thermal-orange pl-5 italic mb-12">
          {post.excerpt}
        </p>

        {post.sections.map((s, i) => (
          <div key={i} className="mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4 text-white">
              {s.h2}
            </h2>
            <p
              className="text-base sm:text-lg text-slate-300 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: s.body.replace(
                  /\*\*(.+?)\*\*/g,
                  '<strong class="text-thermal-orange font-semibold">$1</strong>'
                ),
              }}
            />
          </div>
        ))}

        {/* CTA */}
        <div className="mt-14 border-t border-white/10 pt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-2">
              / Sıradaki Adım
            </div>
            <h3 className="text-xl sm:text-2xl font-bold">
              Tesisinize özel saha analizi alın
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Link
              to="/risk-hesaplayici"
              className="inline-flex items-center justify-center gap-2 bg-thermal-orange hover:bg-thermal-red text-white px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors min-h-[48px]"
            >
              Risk Hesapla
            </Link>
            <Link
              to="/iletisim"
              className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-thermal-orange text-white px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors min-h-[48px]"
            >
              İletişim <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Related */}
      {others.length > 0 && (
        <section className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 pb-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal-orange mb-4">
            / İlgili Yazılar
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {others.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="terminal-border p-5 hover:border-thermal-orange/60 transition-colors flex items-start justify-between gap-3 group"
              >
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    / {p.category}
                  </div>
                  <div className="mt-2 font-semibold group-hover:text-thermal-orange transition-colors leading-tight">
                    {p.title}
                  </div>
                </div>
                <ArrowUpRight className="w-5 h-5 text-thermal-orange shrink-0 mt-1" />
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
